import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useNotification } from 'utils/hooks/useNotification';
import { userAtom } from '../../../utils/hooks/auth/userAtom';
import { ORCHESTRATION_STATUSES } from '../dashboardContants';
import { selectedRowsAtom } from '../atoms';
import { dashboardFilterAtom } from '../filterAtom';
import { stopJob } from './api';

export const useStopOrchestrations = () => {
  const selectedRows = useAtomValue(selectedRowsAtom);
  const user = useAtomValue(userAtom);
  const filters = useAtomValue(dashboardFilterAtom);
  const queryClient = useQueryClient();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const { mutate: stopOrchestrations, isPending } = useMutation({
    mutationFn: async () => {
      const stopPromises = selectedRows.map((row) => stopJob(row.jobId));
      await Promise.all(stopPromises);
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'An unknown error occurred';
      showErrorNotification(`Failed to stop orchestrations: ${message}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      showSuccessNotification('Orchestrations stopped successfully');
    },
  });

  const handleStopOrchestrations = () => {
    if (selectedRows.length === 0) {
      return;
    }
    stopOrchestrations();
  };

  const userCanStop =
    user?.user?.userRole?.permissions?.ORCHESTRATION_STOP === 'Allow';
  const canStopOrchestrations =
    selectedRows.length > 0 &&
    selectedRows.every(
      (row) =>
        row.status === ORCHESTRATION_STATUSES.IN_PROGRESS ||
        row.status === ORCHESTRATION_STATUSES.PENDING
    ) &&
    userCanStop;
  const hideStopButton = filters?.jobStatuses?.some((status) =>
    ['FAILED', 'STOPPED', 'STOPPING', 'FINISHED', 'FINISHED_ERROR'].includes(
      status
    )
  );

  const stopButtonTooltip = !canStopOrchestrations
    ? 'Select one or more rows to stop pending or actively running orchestration instances'
    : 'Stop selected orchestration instances';

  return {
    canStopOrchestrations,
    handleStopOrchestrations,
    hideStopButton,
    isPending,
    stopButtonTooltip,
  };
};
